import  { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./SubscriptionBookingModal.scss";
import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import api from "../../utils/api";
import Login from "../../pages/Login/Login";
import { photosApi, monthNames, weekDays } from "../../SourceData";
import { LockClock } from "@mui/icons-material";
import moment from "moment";
import { toast } from "react-toastify";

const SubscriptionBookingModal = ({
  open,
  onClose,
  subscription,
  onBookingComplete,
}) => {
  // ============ STATE MANAGEMENT ============
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [bookedTimes, setBookedTimes] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarDays, setCalendarDays] = useState([]);
  const [bookingAvailability, setBookingAvailability] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [workingHours, setWorkingHours] = useState({
    from: "09:00",
    to: "17:00",
  });
  const [note, setNote] = useState(""); // New state for notes
  const [currentTime, setCurrentTime] = useState(new Date());

console.log(subscription)

  // ============ HOOKS ============
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const isArabic = language === "ar";

  // ============ HELPER FUNCTIONS ============
  // Get dynamic placeholder based on selected category
  const getNotePlaceholder = () => {
    if (!selectedCategory) {
      return isArabic
        ? "يرجى وصف المشكلة التقنية أو سبب الزيارة بالتفصيل..."
        : "Please describe the technical issue or reason for visit in detail...";
    }

    const categoryName = selectedCategory.name[language]?.toLowerCase() || "";

    // Check for plumbing categories (سباكة)
    if (
      categoryName.includes("سباك") ||
      categoryName.includes("plumb") ||
      categoryName.includes("صحي") ||
      categoryName.includes("مياه") ||
      categoryName.includes("pipes") ||
      categoryName.includes("water")
    ) {
      return isArabic
        ? "مثال: تسريب مياه من صنبور المطبخ مع صوت تقطير مستمر منذ 3 أيام، أو انسداد في بالوعة الحمام مع تجمع المياه ورائحة كريهة، أو ضعف ضغط الماء في الدش..."
        : "Example: Kitchen faucet dripping constantly for 3 days with water pooling, or bathroom sink drain blocked with standing water and bad smell, or low water pressure in shower...";
    }

    // Check for electrical categories (كهرباء)
    if (
      categoryName.includes("كهرب") ||
      categoryName.includes("electric") ||
      categoryName.includes("كهربائي") ||
      categoryName.includes("إضاءة") ||
      categoryName.includes("power") ||
      categoryName.includes("outlet")
    ) {
      return isArabic
        ? "مثال: انقطاع الكهرباء في غرفة النوم الرئيسية وعدم عمل المفاتيح، أو وميض في إضاءة الصالة مع صوت طقطقة من اللوحة الكهربائية، أو عدم عمل مقابس الكهرباء..."
        : "Example: Complete power outage in master bedroom with wall switches not working, or living room lights flickering with crackling sound from electrical panel, or electrical outlets not working...";
    }

    // Check for air conditioning categories (تكييف)
    if (
      categoryName.includes("تكييف") ||
      categoryName.includes("air condition") ||
      categoryName.includes("مكيف") ||
      categoryName.includes("hvac") ||
      categoryName.includes("cooling") ||
      categoryName.includes("heating")
    ) {
      return isArabic
        ? "مثال: المكيف لا يبرد بشكل كافٍ رغم تشغيله لساعات، أو يصدر أصواتاً غريبة وطقطقة، أو تسريب مياه من الوحدة الداخلية على الأرض..."
        : "Example: Air conditioner not cooling properly despite running for hours, or making strange noises and clicking sounds, or water leaking from indoor unit onto the floor...";
    }

    // Check for appliance repair categories (أجهزة منزلية)
    if (
      categoryName.includes("أجهزة") ||
      categoryName.includes("appliance") ||
      categoryName.includes("جهاز") ||
      categoryName.includes("repair") ||
      categoryName.includes("ثلاجة") ||
      categoryName.includes("غسالة")
    ) {
      return isArabic
        ? "مثال: الثلاجة لا تبرد والطعام يفسد بسرعة، أو الغسالة تهتز بشدة ولا تكمل دورة الغسيل، أو الفرن لا يسخن أو يتوقف فجأة..."
        : "Example: Refrigerator not cooling and food spoiling quickly, or washing machine vibrating heavily and not completing wash cycle, or oven not heating or stopping suddenly...";
    }

    // Check for general maintenance categories (صيانة عامة)
    if (
      categoryName.includes("صيانة") ||
      categoryName.includes("maintenance") ||
      categoryName.includes("عامة") ||
      categoryName.includes("general")
    ) {
      return isArabic
        ? "مثال: تسريب في السقف بعد المطر، أو صرير في الأبواب والنوافذ، أو تشقق في الجدران، أو مشاكل في الأقفال..."
        : "Example: Ceiling leak after rain, or squeaking doors and windows, or wall cracks, or lock problems...";
    }

    // Default for other categories
    return isArabic
      ? "اوصف المشكلة أو سبب الزيارة بالتفصيل مع ذكر المكان والأعراض والمدة منذ بداية المشكلة..."
      : "Describe the issue or reason for visit in detail including location, symptoms, and duration since the problem started...";
  };

  // ============ TRANSLATIONS ============
  const translations = useMemo(
    () => ({
      modalTitle: isArabic
        ? "حجز زيارة باستخدام الاشتراك"
        : "Book Visit Using Subscription",
      categorySelectionTitle: isArabic ? "اختر القسم" : "Select Department",
      dateSelectionTitle: isArabic
        ? "اختر الموعد المناسب"
        : "Choose a Suitable Date",
      noteLabel: isArabic
        ? "وصف المشكلة أو سبب الزيارة"
        : "Describe the issue or reason for visit",
      notePlaceholder: getNotePlaceholder(),
      noteRequired: isArabic ? "وصف المشكلة مطلوب" : "Description is required",
      cancelButtonLabel: isArabic ? "إلغاء" : "Cancel",
      confirmButtonLabel: isArabic ? "تأكيد الحجز" : "Confirm Booking",
      availableTimeLabel: isArabic
        ? `الوقت المتاح ليوم ${
            selectedDate ? getFormattedDate(selectedDate) : ""
          }`
        : `Available times for ${
            selectedDate
              ? selectedDate.toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
              : ""
          }`,
      fullyBookedLabel: isArabic
        ? "مواعيد محجوزة بالكامل"
        : "Fully Booked Slots",
      noAvailability: isArabic
        ? "عذرًا، لا توجد مواعيد متاحة في هذا اليوم"
        : "Sorry, no availability for this day",
      noAvailabilityToday: isArabic
        ? "عذرًا، لا يمكن الحجز بعد ساعات العمل"
        : "Sorry, no bookings after working hours",
      pleaseSelectDate: isArabic
        ? "يرجى اختيار تاريخ لعرض المواعيد المتاحة"
        : "Please select a date to see available time slots",
      noCategories: isArabic
        ? "لا توجد أقسام متاحة"
        : "No departments available",
      loading: isArabic ? "جاري التحميل..." : "Loading...",
      errorMessage: isArabic
        ? "حدث خطأ أثناء تحميل المواعيد المتاحة. يرجى المحاولة مرة أخرى."
        : "Error loading available times. Please try again.",
      bookedStatus: isArabic ? "(محجوز)" : "(Booked)",
      pastTimeStatus: isArabic ? "(وقت مضى)" : "(Past time)",
      workingHours: isArabic ? "ساعات العمل" : "Working Hours",
      availableSlots: isArabic ? "المواعيد المتاحة" : "Available Slots",
      noTimesAvailable: isArabic
        ? "لا توجد مواعيد متاحة في هذا اليوم. يرجى اختيار يوم آخر."
        : "No times available for this day. Please select another date.",
      selectCategoryFirst: isArabic
        ? "يرجى اختيار قسم أولاً"
        : "Please select a department first",
      selectDateFirst: isArabic
        ? "يرجى اختيار تاريخ أولاً"
        : "Please select a date first",
    }),
    [isArabic, selectedDate, selectedCategory, language]
  );

  // ============ HELPER FUNCTIONS ============
  // Helper function to check if current time is past working hours
  function isTimePastWorkingHours(currentTime, closingTimeStr) {
    const [closingHour, closingMinute] = closingTimeStr.split(":").map(Number);
    const currentHour = currentTime.getHours();
    const currentMinute = currentTime.getMinutes();

    return (
      currentHour > closingHour ||
      (currentHour === closingHour && currentMinute >= closingMinute)
    );
  }

  // Add helper function to check if a time slot is in the past
  function isTimeSlotPast(timeSlot) {
    // Only check for today
    if (
      !selectedDate ||
      selectedDate.toDateString() !== new Date().toDateString()
    ) {
      return false;
    }

    // Parse the end time of the slot
    const [hours, minutes] = timeSlot.endTime.split(":").map(Number);

    // Create a date object for the slot's end time
    const slotEndTime = new Date();
    slotEndTime.setHours(hours, minutes, 0, 0);

    // Compare with current time
    return currentTime >= slotEndTime;
  }

  // ============ INITIALIZE MODAL ============
  useEffect(() => {
    if (open) {
      // Reset all state when modal opens
      setSelectedCategory(null);
      setSelectedDate(null);
      setSelectedTime(null);
      setNote(""); // Reset note
      setError(null);
      setAvailableTimes([]);
      setBookedTimes([]);

      // Set today's date as default selected date when modal opens
      const today = new Date();
      setSelectedDate(today);
      setCurrentTime(today);
      // Make sure current month is set to display the current month
      setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
      // Fetch categories when modal opens
      fetchCategories();
    }
  }, [open]);

  // Add interval to update current time every minute
  useEffect(() => {
    if (!open) return;
    // Initial current time setting
    setCurrentTime(new Date());
    // Set up an interval to update current time every minute
    const intervalId = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // 60000 ms = 1 minute

    // Clean up interval on unmount or when modal closes
    return () => clearInterval(intervalId);
  }, [open]);

  // ============ API CALLS ============
  // Fetch categories from API
  const fetchCategories = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get("/categories");
      setCategories(response.data.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setError("Failed to load categories");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch available booking slots for selected date and category
  const fetchAvailableSlots = async (date) => {
    if (!date || !selectedCategory ) return;

    setIsLoading(true);
    setError(null);

    try {
      const formattedDate = formatDateForAPI(date);

      const response = await api.get(`/bookings/busySlots`, {
        params: {
          date: formattedDate,
          categoryId: selectedCategory._id,
          addressId: subscription.address._id,
          subscriptionId: subscription._id,
        },
      });

      const {
        bookingAvailability,
        workingHours: apiWorkingHours,
        visitPeriod,
        fullyBookedSlots,
      } = response.data.data;

      // Store working hours for further use
      setWorkingHours(apiWorkingHours);

      // Check if today is past working hours
      const today = new Date();
      if (
        date.toDateString() === today.toDateString() &&
        isTimePastWorkingHours(today, apiWorkingHours.to)
      ) {
        setBookingAvailability(false);
        setAvailableTimes([]);
        setBookedTimes([]);
        return;
      }

      setBookingAvailability(bookingAvailability);

      // Generate available and booked time slots
      const { availableSlots, bookedSlots } = generateTimeSlots(
        apiWorkingHours,
        visitPeriod,
        fullyBookedSlots
      );

      setAvailableTimes(availableSlots);
      setBookedTimes(bookedSlots);
    } catch (error) {
      console.error("Error fetching available slots:", error);
      setError(error.message || "Failed to load available slots");
      setAvailableTimes([]);
      setBookedTimes([]);
      setBookingAvailability(false);
    } finally {
      setIsLoading(false);
    }
  };

  // ============ DATE & TIME FORMATTING ============
  function formatDateForAPI(date) {
    if (!date) return "";

    // Use these methods instead of toISOString() to preserve the local date
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");

    return `${year}-${month}-${day}`; // Returns YYYY-MM-DD in local time
  }

  function formatTime(timeString) {
    if (!timeString || !timeString.includes(":")) {
      console.error("Invalid time format:", timeString);
      return timeString || "";
    }

    const [hours, minutes] = timeString.split(":").map(Number);

    // Handle invalid time values
    if (isNaN(hours) || isNaN(minutes)) {
      console.error("Invalid time components:", timeString);
      return timeString;
    }

    // For Arabic format
    if (isArabic) {
      const period = hours >= 12 ? "مساءً" : "صباحًا";
      const displayHours = hours % 12 || 12;
      return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
    }
    // For English format
    else {
      const period = hours >= 12 ? "PM" : "AM";
      const displayHours = hours % 12 || 12;
      return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
    }
  }

  function getFormattedDate(date) {
    if (!date) return "";
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return date.toLocaleDateString(isArabic ? "ar-SA" : "en-US", options);
  }

  // ============ TIME SLOT GENERATION ============
  function generateTimeSlots(workingHours, visitPeriod, fullyBookedSlots) {
    // Convert working hours to minutes
    const [startHour, startMinute] = workingHours.from.split(":").map(Number);
    const [endHour, endMinute] = workingHours.to.split(":").map(Number);

    // Convert visit period to minutes
    const visitPeriodMinutes = visitPeriod * 60;

    // Calculate total working minutes
    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;

    const availableSlots = [];
    const bookedSlots = [];

    // Normalize booked slots for easier comparison
    const normalizedBookedSlots = Array.isArray(fullyBookedSlots)
      ? fullyBookedSlots.map((slot) => {
          // Handle different time formats (9:00 vs 09:00)
          const normalizedStartTime = slot.startTime.includes(":")
            ? slot.startTime
            : `${slot.startTime}:00`;

          return {
            ...slot,
            startTime: normalizedStartTime,
            // Convert times to lowercase and trim for consistent comparison
            startTimeNormalized: normalizedStartTime.toLowerCase().trim(),
          };
        })
      : [];

    // Generate time slots based on visit period
    for (
      let currentMinutes = startMinutes;
      currentMinutes + visitPeriodMinutes <= endMinutes;
      currentMinutes += visitPeriodMinutes
    ) {
      // Calculate start and end times
      const startHour = Math.floor(currentMinutes / 60);
      const startMinute = currentMinutes % 60;
      const endCalculatedMinutes = currentMinutes + visitPeriodMinutes;
      const endHour = Math.floor(endCalculatedMinutes / 60);
      const endMinute = endCalculatedMinutes % 60;

      // Format time strings
      const startTime = `${startHour.toString().padStart(2, "0")}:${startMinute
        .toString()
        .padStart(2, "0")}`;
      const endTime = `${endHour.toString().padStart(2, "0")}:${endMinute
        .toString()
        .padStart(2, "0")}`;

      // Check if this slot is fully booked - with improved comparison
      const isBooked = normalizedBookedSlots.some((slot) => {
        // Try different formats of time comparison to ensure matching
        return (
          // Direct comparison
          (slot.startTimeNormalized === startTime.toLowerCase() ||
            // Compare without leading zeros
            slot.startTimeNormalized ===
              `${parseInt(startTime.split(":")[0])}:${
                startTime.split(":")[1]
              }`.toLowerCase() ||
            // Compare with different separators
            slot.startTimeNormalized.replace(":", "") ===
              startTime.replace(":", "").toLowerCase()) &&
          slot.allTechniciansBusy
        );
      });

      const timeSlot = {
        formatted: formatTime(startTime),
        value: startTime,
        endTime: endTime,
        formattedEndTime: formatTime(endTime),
      };

      // Sort into appropriate array based on availability
      if (isBooked) {
        bookedSlots.push(timeSlot);
      } else {
        availableSlots.push(timeSlot);
      }
    }

    return { availableSlots, bookedSlots };
  }

  // ============ CALENDAR GENERATION ============
  useEffect(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    // First and last day of the month
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Adjust for RTL calendar (week starts on Saturday)
    let firstDayOfWeek = (firstDay.getDay() + 1) % 7;

    const days = [];

    // Add empty slots for days before first day of month
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push({ day: null, date: null });
    }

    // Add days of the month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const date = new Date(year, month, i);
      const today = new Date();
      // Reset time to midnight for accurate date comparison
      today.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);

      // Only disable past dates
      const isPastDate = date < today;

      days.push({
        day: i,
        date: date,
        isDisabled: isPastDate,
        isToday: date.toDateString() === today.toDateString(),
      });
    }

    setCalendarDays(days);
  }, [currentMonth]);

  // ============ EFFECTS ============
  // Fetch available slots when date or category changes
  useEffect(() => {
    if (selectedDate && selectedCategory) {
      fetchAvailableSlots(selectedDate);
    } else {
      setAvailableTimes([]);
      setBookedTimes([]);
    }
  }, [selectedDate, selectedCategory]);

  // ============ EVENT HANDLERS ============
  const prevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  // Handle category selection
  const handleCategorySelect = (category) => {
    // If user is not authenticated, show login modal
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    setSelectedCategory(category);
    setSelectedTime(null); // Reset time when category changes
  };

  // Handle date selection
  const handleDateClick = (day) => {
    // If user is not authenticated, show login modal
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    if (day && !day.isDisabled) {
      setSelectedDate(day.date);
      setSelectedTime(null); // Reset time when date changes
    }
  };

  // Handle time selection
  const handleTimeClick = (time) => {
    // If user is not authenticated, show login modal
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    // Don't allow selecting past time slots
    if (isTimeSlotPast(time)) {
      return;
    }

    setSelectedTime(time);
  };

  // Handle note change
  const handleNoteChange = (e) => {
    setNote(e.target.value);
  };

  // Handle login modal close
  const handleLoginClose = () => {
    setShowLoginModal(false);
  };

  // Confirm booking using subscription method
  const handleConfirmBooking = async () => {
    // Check authentication again just to be safe
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    // Validate all required fields including note
    if (
      !selectedDate ||
      !selectedTime ||
      !selectedCategory ||
      !subscription ||
      !note.trim()
    ) {
      if (!note.trim()) {
        setError(translations.noteRequired);
      }
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const bookingData = {
        subScription: subscription._id,
        category: selectedCategory._id,
        date: formatDateForAPI(selectedDate),
        startTime: selectedTime.value,
        notes: note.trim(), // Include the note in booking data
      };

      // Make API call to create booking using subscription
      await api.post("/bookings/checkout-byPackage", bookingData);
      toast.success(t("bookingConfirmed"));
      // Close modal
      onClose();

      // Use the callback if provided, otherwise navigate
      if (typeof onBookingComplete === "function") {
        onBookingComplete();
      } else {
        // Navigate to appropriate page (maybe bookings history)
        navigate("/bookings");
      }
    } catch (error) {
      console.error("Error creating booking:", error);
      setError("Failed to create booking");
    } finally {
      setIsLoading(false);
    }
  };

  // ============ RENDER HELPERS ============
  // Render category selection
  const renderCategorySelection = () => {
    if (isLoading && categories.length === 0) {
      return <div className="loading-state">{translations.loading}</div>;
    }

    if (error && categories.length === 0) {
      return <div className="error-state">{error}</div>;
    }

    if (categories.length === 0) {
      return <div className="no-categories">{translations.noCategories}</div>;
    }

    return (
      <div className="category-selection">
        <h3 className="section-title">{translations.categorySelectionTitle}</h3>
        <div className="category-grid">
          {categories.map((category) => (
            <div
              key={category._id}
              className={`category-card ${
                selectedCategory && selectedCategory._id === category._id
                  ? "selected"
                  : ""
              }`}
              onClick={() => handleCategorySelect(category)}
              role="button"
              aria-pressed={
                selectedCategory && selectedCategory._id === category._id
              }
            >
              <div className="category-icon">
                {category.photo && (
                  <img
                    src={photosApi + category.photo}
                    alt={category.name[language]}
                  />
                )}
              </div>
              <div className="category-name">{category.name[language]}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render time selection
  const renderTimeSelection = () => {
    if (!selectedCategory) {
      return (
        <div className="no-category-selected">
          {translations.selectCategoryFirst}
        </div>
      );
    }

    if (!selectedDate) {
      return (
        <div className="no-date-selected">{translations.selectDateFirst}</div>
      );
    }

    const formattedFromTime = moment(workingHours.from, "HH:mm").format(
      "h:mm A"
    );
    const formattedToTime = moment(workingHours.to, "HH:mm").format("h:mm A");

    if (isLoading) {
      return <div className="loading-state">{translations.loading}</div>;
    }

    if (error && error !== translations.noteRequired) {
      return <div className="error-state">{error}</div>;
    }

    // Special handling for today being past working hours
    const today = new Date();
    if (
      selectedDate &&
      selectedDate.toDateString() === today.toDateString() &&
      !bookingAvailability
    ) {
      return (
        <div className="working-hours-container">
          <div className="working-hours-header">
            <LockClock className="working-hours-icon" />
            <h3 className="working-hours-title">{translations.workingHours}</h3>
          </div>
          <div className="working-hours-display">
            <div className="working-hours-time">
              <span className="working-hours-label">
                {isArabic ? "من" : "From"}:
              </span>
              <span className="working-hours-value">{formattedFromTime}</span>
            </div>
            <div className="working-hours-time">
              <span className="working-hours-label">
                {isArabic ? "إلى" : "To"}:
              </span>
              <span className="working-hours-value">{formattedToTime}</span>
            </div>
          </div>
          <div className="no-availability">
            {translations.noAvailabilityToday}
          </div>
        </div>
      );
    }

    if (!bookingAvailability) {
      return (
        <div className="no-availability">{translations.noAvailability}</div>
      );
    }

    return (
      <div className="time-selection">
        {/* Section Title */}
        <h4 className="field-label">
          {selectedDate
            ? translations.availableTimeLabel
            : translations.pleaseSelectDate}
        </h4>

        {/* Available time slots */}
        {availableTimes.length > 0 && (
          <div className="available-slots">
            <h5 className="slots-heading">{translations.availableSlots}</h5>
            <div className="time-grid">
              {availableTimes.map((time) => {
                const isPastTimeSlot = isTimeSlotPast(time);
                return (
                  <button
                    key={time.value}
                    className={`time-option ${
                      selectedTime && selectedTime.value === time.value
                        ? "selected"
                        : ""
                    } ${isPastTimeSlot ? "past-time" : ""}`}
                    onClick={() => handleTimeClick(time)}
                    aria-selected={
                      selectedTime && selectedTime.value === time.value
                    }
                    disabled={isPastTimeSlot}
                    aria-disabled={isPastTimeSlot}
                  >
                    <>
                      <span className="time-range">
                        {isArabic
                          ? `من ${time.formatted} إلى ${time.formattedEndTime}`
                          : `${time.formatted} - ${time.formattedEndTime}`}
                      </span>
                      {isPastTimeSlot && (
                        <span className="time-status no-wrap">
                          {translations.pastTimeStatus}
                        </span>
                      )}
                    </>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Fully booked time slots */}
        {bookedTimes.length > 0 && (
          <div className="booked-slots">
            <h5 className="slots-heading">{translations.fullyBookedLabel}</h5>
            <div className="time-grid booked-grid">
              {bookedTimes.map((time) => {
                const isPastTimeSlot = isTimeSlotPast(time);
                return (
                  <button
                    key={time.value}
                    className={`time-option booked ${
                      isPastTimeSlot ? "past-time" : ""
                    }`}
                    disabled
                    aria-disabled="true"
                  >
                    <>
                      <span className="time-range">
                        {isArabic
                          ? `من ${time.formatted} إلى ${time.formattedEndTime}`
                          : `${time.formatted} - ${time.formattedEndTime}`}
                      </span>
                      {isPastTimeSlot ? (
                        <span className="time-status no-wrap">
                          {translations.pastTimeStatus}
                        </span>
                      ) : (
                        <span className="time-status no-wrap">
                          {translations.bookedStatus}
                        </span>
                      )}
                    </>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {availableTimes.length === 0 &&
          bookedTimes.length === 0 &&
          !isLoading &&
          !error &&
          selectedDate && (
            <div className="no-times">{translations.noTimesAvailable}</div>
          )}
      </div>
    );
  };

  // Render notes section
  const renderNotesSection = () => {
    return (
      <div className="notes-section">
        <h4 className="field-label">
          {translations.noteLabel} <span className="required">*</span>
        </h4>
        <textarea
          className={`note-textarea ${
            error === translations.noteRequired ? "error" : ""
          }`}
          placeholder={translations.notePlaceholder}
          value={note}
          onChange={handleNoteChange}
          rows={4}
          maxLength={500}
          required
        />
        <div className="note-char-count">{note.length}/500</div>
        {error === translations.noteRequired && (
          <div className="field-error">{translations.noteRequired}</div>
        )}
      </div>
    );
  };

  // Don't render if modal is closed
  if (!open) return null;

  // ============ RENDER ============
  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div
          className="booking-modal single-step"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-labelledby="booking-modal-title"
        >
          <div className="modal-header">
            <h2 className="modal-title" id="booking-modal-title">
              {translations.modalTitle}
            </h2>
            <button
              className="close-button"
              onClick={onClose}
              aria-label={translations.cancelButtonLabel}
            >
              <span>×</span>
            </button>
          </div>

          <div className="booking-content single-step-content">
            {/* Left Column: Category Selection */}
            <div className="booking-column category-column">
              {renderCategorySelection()}
            </div>

            {/* Right Column: Date, Time, and Notes */}
            <div className="booking-column datetime-column">
              {/* Date Selection */}
              <div className="date-time-selection">
                <h3 className="section-title">
                  {translations.dateSelectionTitle}
                </h3>

                {/* Calendar */}
                <div
                  className="custom-calendar"
                  role="application"
                  aria-label="Calendar"
                >
                  
                  <div className="calendar-header">
                    <button
                      className="month-nav"
                      onClick={prevMonth}
                      aria-label={isArabic ? "الشهر السابق" : "Previous month"}
                    >
                      &#9656;
                    </button>
                    <h4 className="current-month">
                      {monthNames[currentMonth.getMonth()]}{" "}
                      {currentMonth.getFullYear()}
                    </h4>
                    <button
                      className="month-nav"
                      onClick={nextMonth}
                      aria-label={isArabic ? "الشهر التالي" : "Next month"}
                    >
                      &#9666;
                    </button>
                  </div>

                  <div className="calendar-weekdays" role="row">
                    {weekDays.map((day) => (
                      <div key={day} className="weekday" role="columnheader">
                        {day}
                      </div>
                    ))}
                  </div>

                  <div className="calendar-days" role="grid">
                    {calendarDays.map((day, index) => (
                      <div
                        key={index}
                        className={`calendar-day ${!day.day ? "empty" : ""} ${
                          day.isDisabled ? "disabled" : ""
                        } ${day.isToday ? "today" : ""} ${
                          selectedDate &&
                          day.date &&
                          day.date.toDateString() ===
                            selectedDate.toDateString()
                            ? "selected"
                            : ""
                        }`}
                        onClick={() => handleDateClick(day)}
                        tabIndex={day.day && !day.isDisabled ? 0 : -1}
                        role={day.day ? "gridcell" : "presentation"}
                        aria-selected={
                          selectedDate &&
                          day.date &&
                          day.date.toDateString() ===
                            selectedDate.toDateString()
                        }
                        aria-disabled={day.isDisabled}
                      >
                        {day.day}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Time Selection */}
                {renderTimeSelection()}

                {/* Notes Section */}
                {renderNotesSection()}

                {/* Booking Confirmation */}
                {selectedCategory && selectedDate && selectedTime && (
                  <div className="booking-confirmation" aria-live="polite">
                    <p className="confirmation-text">
                      {isArabic
                        ? `سيتم تأكيد حجزك في قسم ${
                            selectedCategory.name[language]
                          } ليوم ${getFormattedDate(selectedDate)} من الساعة ${
                            selectedTime.formatted
                          } إلى ${selectedTime.formattedEndTime}`
                        : `Your booking will be confirmed for ${
                            selectedCategory.name[language]
                          } on ${getFormattedDate(selectedDate)} from ${
                            selectedTime.formatted
                          } to ${selectedTime.formattedEndTime}`}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="modal-actions">
            <button className="cancel-btn" onClick={onClose}>
              {translations.cancelButtonLabel}
            </button>

            <button
              className={`confirm-btn ${
                !selectedCategory ||
                !selectedDate ||
                !selectedTime ||
                !note.trim() ||
                isLoading
                  ? "disabled"
                  : ""
              }`}
              disabled={
                !selectedCategory ||
                !selectedDate ||
                !selectedTime ||
                !note.trim() ||
                isLoading
              }
              onClick={handleConfirmBooking}
            >
              {isLoading
                ? isArabic
                  ? "جاري التحميل..."
                  : "Loading..."
                : translations.confirmButtonLabel}
            </button>
          </div>

  

        </div>
      </div>

      {/* Login Modal */}
      {showLoginModal && (
        <Login open={showLoginModal} onClose={handleLoginClose} />
      )}
    </>
  );
};

export default SubscriptionBookingModal;
